import React, { useEffect, useState } from "react";
import {
  Dialog,
  DialogContent,
  Typography,
  IconButton,
  TextField,
  Button,
  Box,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { toast } from "react-toastify";
import { StyledTextField } from "@components/CustomInput/customInput";

interface EmailVerificationModalProps {
  open: boolean;
  email: string;
  onClose: () => void;
  onSendOtp: () => Promise<{ success: boolean; message?: string }>;
  onVerifyOtp: (otp: string) => Promise<{ success: boolean; message?: string }>;
  onEmailChange: (email: string) => void;
}

const OTP_LENGTH = 5;

const maskEmail = (email: string) => {
  const [name, domain] = email.split("@");
  return `${name.slice(0, 2)}*****@${domain}`;
};

const EmailVerificationModal: React.FC<EmailVerificationModalProps> = ({
  open,
  email,
  onClose,
  onSendOtp,
  onVerifyOtp,
  onEmailChange,
}) => {
  const [step, setStep] = useState<"EMAIL" | "OTP">("EMAIL");
  const [otp, setOtp] = useState<string[]>(Array(OTP_LENGTH).fill(""));
  const [emailError, setEmailError] = useState<string>("");
  const [timer, setTimer] = useState(59);
  const [initialEmail, setInitialEmail] = useState<string>(email);

  useEffect(() => {
    if (open) {
      setInitialEmail(email);
    }
  }, [open]);

  const isSendDisabled = Boolean(emailError) || !email;
  const isEditedNow = email.trim() !== initialEmail.trim();

  // Countdown
  useEffect(() => {
    if (step === "OTP" && timer > 0) {
      const interval = setInterval(() => setTimer((t) => t - 1), 1000);
      return () => clearInterval(interval);
    }
  }, [step, timer]);

  useEffect(() => {
    setOtp(Array(OTP_LENGTH).fill(""));
  }, [step]);

  const handleOtpChange = (value: string, index: number) => {
    if (!/^\d?$/.test(value)) return;

    const newOtp = [...otp];
    newOtp[index] = value;
    setOtp(newOtp);

    if (value && index < otp.length - 1) {
      document.getElementById(`otp-${index + 1}`)?.focus();
    }
  };

  const handleOtpKeyDown = (
    e: React.KeyboardEvent<HTMLInputElement | HTMLDivElement>,
    index: number,
  ) => {
    if (e.key === "Backspace") {
      e.preventDefault();

      const newOtp = [...otp];

      if (newOtp[index]) {
        newOtp[index] = "";
        setOtp(newOtp);
      } else if (index > 0) {
        newOtp[index - 1] = "";
        setOtp(newOtp);
        document.getElementById(`otp-${index - 1}`)?.focus();
      }
    }
  };

  const handleVerify = async () => {
    const response = await onVerifyOtp(otp.join(""));

    if (response?.success) {
      onClose();
      setOtp(Array(OTP_LENGTH).fill(""));
      setStep("EMAIL");
    }
  };

  const handleSendOtpClick = async () => {
    try {
      const response = await onSendOtp();

      if (response?.success) {
        toast.success("OTP Sent Successfully to Email Address.");
        setTimer(59);
        setStep("OTP");
      }
    } catch (error: any) {
      toast.error(error?.message || "Failed to send OTP");
    }
  };

  const validateEmail = (value: string) => {
    if (!value.trim()) return "Email is required";
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(value)) return "Enter a valid email address";
    return "";
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="xs"
      fullWidth
      PaperProps={{ sx: { borderRadius: "16px" } }}
    >
      <DialogContent sx={{ p: 3 }}>
        {/* Header */}
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          mb={2}
        >
          <Typography fontWeight={600} fontSize={18}>
            {step === "EMAIL" ? "Enter Email Address" : "Enter OTP"}
          </Typography>
          <IconButton
            onClick={() => {
              onClose();
              setStep("EMAIL");
            }}
          >
            <CloseIcon sx={{ color: "#000" }} />
          </IconButton>
        </Box>

        {/* EMAIL STEP */}
        {step === "EMAIL" && (
          <>
            <StyledTextField
              fullWidth
              size="small"
              type="email"
              value={email}
              onChange={(e) => {
                const value = e.target.value;
                onEmailChange(value);
                setEmailError(validateEmail(value));
              }}
              onBlur={() => {
                setEmailError(validateEmail(email));
              }}
              placeholder="Enter email"
              sx={{ mb: 3 }}
              error={Boolean(emailError)}
              helperText={emailError}
              required
            />

            <Button
              fullWidth
              variant="contained"
              sx={{
                height: 44,
                borderRadius: 24,
                textTransform: "none",
                // backgroundColor: "#4B1E8A",
                fontSize: 14,
                backgroundColor: "#4B1E8A",
                "&:hover": {
                  backgroundColor: "#4B1E8A",
                },
                "&.Mui-disabled": {
                  backgroundColor: "#757575",
                  color: "#fff",
                },
              }}
              onClick={handleSendOtpClick}
              disabled={Boolean(emailError) || !email || !isEditedNow}
            >
              Send OTP to verify
            </Button>
          </>
        )}

        {/* OTP STEP */}
        {step === "OTP" && (
          <>
            <Typography fontSize={13} color="#212121" mb={2}>
              Enter the OTP sent to your email:{" "}
              <strong>{maskEmail(email)}</strong>
            </Typography>

            <Typography
              fontSize={13}
              color="#000"
              sx={{
                cursor: "pointer",
                mb: 2,
                textAlign: "center",
                fontSize: "14px",
                fontWeight: 600,
              }}
            >
              Wrong Email?{" "}
              <span
                style={{
                  color: "#3a1c73",
                  fontWeight: 600,
                }}
                onClick={() => setStep("EMAIL")}
              >
                Change it
              </span>
            </Typography>

            {/* OTP Boxes */}
            <Box display="flex" justifyContent="center" gap="10px" mb={2.5}>
              {otp.map((digit, i) => (
                <TextField
                  key={i}
                  id={`otp-${i}`}
                  value={digit}
                  onChange={(e) => handleOtpChange(e.target.value, i)}
                  onKeyDown={(e) => handleOtpKeyDown(e, i)}
                  inputProps={{
                    maxLength: 1,
                    inputMode: "numeric",
                    style: {
                      textAlign: "center",
                      fontSize: "16px",
                      padding: 0,
                    },
                  }}
                  sx={{
                    width: 44,
                    "& .MuiOutlinedInput-root": {
                      height: 44,
                      borderRadius: "8px",
                    },
                  }}
                />
              ))}
            </Box>

            <Typography fontSize={12} color="#212121" mb={2} textAlign="center">
              Didn’t receive the OTP?{" "}
              {timer > 0 ? (
                <>Resend in 00:{timer.toString().padStart(2, "0")}</>
              ) : (
                <span
                  style={{ color: "#4B1E8A", cursor: "pointer" }}
                  onClick={() => {
                    setTimer(59);
                    handleSendOtpClick();
                  }}
                >
                  Resend
                </span>
              )}
            </Typography>

            <Button
              fullWidth
              variant="contained"
              sx={{
                height: 44,
                borderRadius: 24,
                textTransform: "none",
                backgroundColor: "#4B1E8A",
              }}
              onClick={handleVerify}
            >
              Verify
            </Button>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default EmailVerificationModal;
